import React, { useEffect, useMemo, useState } from 'react';
import ECharts from 'echarts-for-react';
import { GetTime } from '../lib/DeviceData';
import moment from 'moment';

const HEART_COLOR = '#F75C5D';
const BREATH_COLOR = '#3889ff';
const DEFAULT_SIZE = 60;

const normalizeRows = res => {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.rows)) return res.rows;
    return [];
};

const pickHr = row => {
    const v = row.hr_avg ?? row.hr ?? row.heart;
    return v == null || v === '' ? null : Number(v);
};

const pickRr = row => {
    const v = row.rr_avg ?? row.rr ?? row.resp;
    return v == null || v === '' ? null : Number(v);
};

const pickTime = row => row.bucket ?? row.time ?? row.ts ?? row.reg_date;

const HistoryLog = props => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const data = props.data;
        if (!data || data.device_no == null || !data.date || data.time === '' || data.time == null) {
            setRows([]);
            setError(null);
            return;
        }

        const device_no = data.device_no;
        const date = data.date;
        const time = data.time;
        const size = data.size != null ? Number(data.size) : DEFAULT_SIZE;
        const start_date = moment(`${date} ${time}:00:00`).utc().format('YYYY-MM-DD HH:mm:ss');
        const end_date = moment(`${date} ${time}:59:59`).utc().format('YYYY-MM-DD HH:mm:ss');

        setLoading(true);
        setError(null);

        GetTime(
            device_no,
            start_date,
            end_date,
            { size },
            res => {
                setRows(normalizeRows(res));
                setLoading(false);
            },
            err => {
                const msg =
                    err?.response?.data?.message || err?.message || '이력 조회에 실패했습니다.';
                setError(msg);
                setRows([]);
                setLoading(false);
            },
        );
    }, [props.data]);

    const chart = useMemo(() => {
        const labels = [];
        const hrSeries = [];
        const rrSeries = [];

        const sorted = [...rows].sort((a, b) => {
            const ta = moment(pickTime(a)).valueOf();
            const tb = moment(pickTime(b)).valueOf();
            return ta - tb;
        });

        sorted.forEach(row => {
            const t = pickTime(row);
            labels.push(t ? moment(t).format('HH:mm:ss') : '-');
            hrSeries.push(pickHr(row));
            rrSeries.push(pickRr(row));
        });

        return {
            labels,
            hrSeries,
            rrSeries,
            count: sorted.length,
        };
    }, [rows]);

    const option = useMemo(
        () => ({
            color: [HEART_COLOR, BREATH_COLOR],
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross' },
            },
            legend: {
                data: ['HR avg', 'RR avg'],
                top: 0,
                right: 0,
                textStyle: { fontFamily: 'Pretendard', fontSize: 12 },
            },
            grid: {
                top: 36,
                left: 48,
                right: 24,
                bottom: 40,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: chart.labels,
                axisLabel: {
                    color: '#777',
                    fontSize: 11,
                    fontFamily: 'Pretendard',
                    hideOverlap: true,
                },
                axisLine: { lineStyle: { color: '#ddd' } },
            },
            yAxis: {
                type: 'value',
                name: 'BPM',
                min: 0,
                axisLabel: {
                    color: '#777',
                    fontSize: 11,
                    fontFamily: 'Pretendard',
                },
                splitLine: { lineStyle: { color: '#eee' } },
            },
            dataZoom: [
                { type: 'inside', start: 0, end: 100 },
                {
                    type: 'slider',
                    height: 18,
                    bottom: 8,
                    start: 0,
                    end: 100,
                },
            ],
            series: [
                {
                    name: 'HR avg',
                    type: 'line',
                    smooth: true,
                    showSymbol: chart.hrSeries.length <= 40,
                    symbolSize: 6,
                    connectNulls: false,
                    lineStyle: { width: 2, color: HEART_COLOR },
                    data: chart.hrSeries,
                },
                {
                    name: 'RR avg',
                    type: 'line',
                    smooth: true,
                    showSymbol: chart.rrSeries.length <= 40,
                    symbolSize: 6,
                    connectNulls: false,
                    lineStyle: { width: 2, color: BREATH_COLOR },
                    data: chart.rrSeries,
                },
            ],
        }),
        [chart],
    );

    if (props.data?.time === '' || props.data?.time == null) {
        return (
            <div className="history_graph_empty" style={{ padding: '24px 0', color: '#999', textAlign: 'center' }}>
                시간을 선택하면 평균 심박/호흡 그래프를 표시합니다.
            </div>
        );
    }

    return (
        <div className="history_graph_wrap">
            <div
                className="history_graph_meta"
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                    fontSize: 13,
                    color: '#777',
                }}
            >
                <span>
                    {loading
                        ? '불러오는 중…'
                        : error
                          ? error
                          : `샘플 ${chart.count}건 · hr_avg / rr_avg`}
                </span>
            </div>
            {!loading && !error && chart.count === 0 ? (
                <div style={{ padding: '24px 0', color: '#999', textAlign: 'center' }}>
                    해당 시간대 이력이 없습니다.
                </div>
            ) : (
                <ECharts
                    option={option}
                    opts={{ renderer: 'svg', width: 'auto' }}
                    style={{ height: 280, width: '100%' }}
                    notMerge
                />
            )}
        </div>
    );
};

export default HistoryLog;
